import { cn } from '@/lib/utils'
import LinkWithTransition from '@/v2/components/helpers/link-with-transition'
import ResponsiveImage from '@/v2/components/helpers/responsive-image'
import SectionHead from '@/v2/components/sections/landing-pages/section-head'
import { Button } from '@/v2/components/ui/button'
import SectionWrapper from '@/v2/components/wrappers/section-wrapper'
import { CardType } from '@/v2/types/cards'
import { motion } from 'framer-motion'
import React from 'react'

interface PopularDestinationsSectionProps {
  title: string
  description?: string
  cards: CardType[]
  className?: string
  ctaButton?: { href: string; text: string }
  isBgDark?: boolean
  imgClassName?: string
  sectionTitleClassName?: string
}

const PopularDestinationsSection: React.FC<PopularDestinationsSectionProps> = props => {
  const {
    title,
    description,
    cards,
    className,
    ctaButton,
    imgClassName,
    isBgDark = false,
    sectionTitleClassName
  } = props

  const cardBackground = isBgDark ? 'bg-[#fff]' : 'bg-[#F9F8F8]'

  return (
    <motion.section className={cn('landing-pages-wr section-spacing', isBgDark ? 'bg-[#F9F8F8]' : '', className)}>
      <SectionWrapper>
        <SectionHead title={title} description={description} className={sectionTitleClassName} />

        <div className='grid xl-min:!grid-cols-3 md-mn:grid-cols-2 grid-cols-1 gap-5'>
          {cards.map(card => {
            return (
              <motion.article
                key={card.id}
                whileInView='visible'
                initial='hidden'
                viewport={{ once: false, amount: 0.2 }}
                className={cn(`transition-all rounded-md shadow-md hover:scale-[1.05]`, cardBackground)}
              >
                <div className='flex flex-col h-full'>
                  {card.image && (
                    <div className='relative w-full'>
                      <ResponsiveImage
                        src={card.image.src}
                        alt={card.image.alt}
                        className={cn(
                          'w-full h-full object-cover transition-transform duration-300 ease-in-out group-hover:scale-110',
                          imgClassName
                        )}
                      />
                    </div>
                  )}

                  <div className='flex-grow py-6 md-mn:px-5 px-2 [&>h3]:mb-3 xl-min:[&>h3]:text-xl md-mn:text-left text-center [&>h3]:text-lg [&>h3]:font-semibold [&>h3]:text-paragraph [&>h3]:leading-8'>
                    <h3>{card.title}</h3>
                    {card.description && (
                      <p className='text-paragraph leading-6 text-base font-normal'>{card.description}</p>
                    )}
                    {card.points && (
                      <ul className='text-paragraph leading-6 text-base font-normal list-inside list-disc'>
                        {card.points?.map(point => <li key={point} dangerouslySetInnerHTML={{ __html: point }} />)}
                      </ul>
                    )}
                  </div>
                </div>
              </motion.article>
            )
          })}
        </div>

        {ctaButton && (
          <LinkWithTransition href={ctaButton.href} className='mt-12 flex items-center justify-center'>
            <Button variant='call_to_action' size='lg' className='transition-all animate-bounce self-center'>
              {ctaButton.text}
            </Button>
          </LinkWithTransition>
        )}
      </SectionWrapper>
    </motion.section>
  )
}

export default PopularDestinationsSection
